import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface MockLead {
  id: string;
  city: string;
  state: string;
  zip: string;
  billRange: string;
  timeline: string;
  leadScore: number;
  solarScore: number;
  price: number;
  status: "available" | "hot";
}

const MOCK_LEADS: MockLead[] = [
  { id: "1", city: "Austin", state: "TX", zip: "78701", billRange: "$250–$300/mo", timeline: "Within 1 month", leadScore: 96, solarScore: 94, price: 185, status: "hot" },
  { id: "2", city: "Phoenix", state: "AZ", zip: "85001", billRange: "$300–$350/mo", timeline: "ASAP", leadScore: 92, solarScore: 97, price: 175, status: "available" },
  { id: "3", city: "Tampa", state: "FL", zip: "33601", billRange: "$200–$250/mo", timeline: "1–3 months", leadScore: 88, solarScore: 91, price: 165, status: "available" },
  { id: "4", city: "Denver", state: "CO", zip: "80201", billRange: "$180–$220/mo", timeline: "Within 1 month", leadScore: 94, solarScore: 89, price: 170, status: "hot" },
  { id: "5", city: "Charlotte", state: "NC", zip: "28201", billRange: "$220–$270/mo", timeline: "ASAP", leadScore: 91, solarScore: 93, price: 180, status: "available" },
  { id: "6", city: "Las Vegas", state: "NV", zip: "89101", billRange: "$280–$330/mo", timeline: "1–3 months", leadScore: 85, solarScore: 96, price: 160, status: "available" },
];

function ScorePill({ score, type }: { score: number; type: "lead" | "solar" }) {
  const color =
    score >= 90 ? "bg-green-100 text-green-700"
    : score >= 75 ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {type === "lead" ? <i className="ri-user-star-line"></i> : <i className="ri-sun-line"></i>}
      {score}
    </span>
  );
}

function LeadCard({ lead, index }: { lead: MockLead; index: number }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <div
      className="bg-white border border-gray-100 rounded-2xl p-5 transition-all duration-300 cursor-pointer"
      style={{
        opacity: 0,
        transform: "translateY(20px)",
        animation: `fadeSlideUp 0.5s ease forwards ${index * 0.1}s`,
        borderColor: hovered ? "#16a34a33" : undefined,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-map-pin-line text-[#16a34a] text-sm"></i>
            <span className="font-bold text-gray-900 text-sm">{lead.city}, {lead.state}</span>
            {lead.status === "hot" && (
              <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                <i className="ri-fire-line"></i> HOT
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400">ZIP {lead.zip}</span>
        </div>
        <span className="text-xl font-bold text-[#16a34a]">${lead.price}</span>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-400 mb-1">Monthly Bill</p>
          <p className="font-semibold text-gray-800">{lead.billRange}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-400 mb-1">Timeline</p>
          <p className="font-semibold text-gray-800">{lead.timeline}</p>
        </div>
      </div>

      {/* Scores */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400">Lead</span>
          <ScorePill score={lead.leadScore} type="lead" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400">Solar</span>
          <ScorePill score={lead.solarScore} type="solar" />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <i className="ri-eye-off-line"></i>
          <span>Contact hidden until purchase</span>
        </div>
        <button
          onClick={() => navigate("/signup")}
          className="bg-[#16a34a] hover:bg-[#15803d] text-white px-4 py-2 rounded-lg text-xs font-semibold transition cursor-pointer whitespace-nowrap"
        >
          Buy Lead
        </button>
      </div>
    </div>
  );
}

export default function DashboardPreview() {
  const [activeFilter, setActiveFilter] = useState("all");
  const navigate = useNavigate();

  const filtered = MOCK_LEADS.filter((l) => {
    if (activeFilter === "hot") return l.status === "hot";
    if (activeFilter === "high") return l.leadScore >= 90;
    return true;
  });

  return (
    <section id="dashboard-preview" className="py-20 md:py-28 bg-[#f8faf8]">
      <style>{`
        @keyframes fadeSlideUp {
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5 mb-4">
            <span className="w-2 h-2 rounded-full bg-[#16a34a] animate-pulse flex-shrink-0"></span>
            <span className="text-green-700 text-sm font-medium">Live Marketplace Preview</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            This is exactly what you see inside
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Real lead cards from the marketplace — filter, score, and buy in seconds. No fluff, no mystery.
          </p>
        </div>

        {/* Browser chrome mockup */}
        <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white">
          {/* Browser bar */}
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-400"></span>
              <span className="w-3 h-3 rounded-full bg-amber-400"></span>
              <span className="w-3 h-3 rounded-full bg-green-400"></span>
            </div>
            <div className="flex-1 bg-white rounded-md px-3 py-1.5 text-xs text-gray-400 border border-gray-200 max-w-xs mx-auto text-center">
              app.solarleads.com/dashboard/leads
            </div>
          </div>

          {/* Dashboard inner */}
          <div className="flex min-h-[520px]">
            {/* Sidebar */}
            <div className="hidden md:flex flex-col w-52 bg-gray-50 border-r border-gray-100 p-4 gap-1 flex-shrink-0">
              <div className="flex items-center gap-2 px-3 py-2 mb-4">
                <div className="w-7 h-7 flex items-center justify-center bg-[#16a34a] rounded-lg">
                  <i className="ri-sun-line text-white text-sm"></i>
                </div>
                <span className="font-bold text-gray-900 text-sm">SolarLead<span className="text-[#16a34a]">Source</span></span>
              </div>
              {[
                { icon: "ri-store-2-line", label: "Available Leads", active: true },
                { icon: "ri-shopping-bag-line", label: "Purchased Leads", active: false },
                { icon: "ri-bank-card-line", label: "Billing", active: false },
                { icon: "ri-settings-3-line", label: "Settings", active: false },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm cursor-pointer transition ${
                    item.active
                      ? "bg-green-50 text-[#16a34a] font-semibold"
                      : "text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={item.icon}></i>
                  </div>
                  {item.label}
                </div>
              ))}
            </div>

            {/* Main content */}
            <div className="flex-1 p-5 md:p-6 bg-[#f8f9fa] overflow-hidden">
              {/* Top bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Available Leads</h3>
                  <p className="text-gray-400 text-xs mt-0.5">{filtered.length} exclusive leads available</p>
                </div>
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
                  <div className="w-2 h-2 bg-[#16a34a] rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-green-700">Live marketplace</span>
                </div>
              </div>

              {/* Filter tabs */}
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {[
                  { key: "all", label: "All Leads" },
                  { key: "hot", label: "Hot Leads" },
                  { key: "high", label: "Score 90+" },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setActiveFilter(f.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer whitespace-nowrap ${
                      activeFilter === f.key
                        ? "bg-[#16a34a] text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-400 bg-white border border-gray-200 px-3 py-1.5 rounded-lg">
                  <i className="ri-refresh-line"></i>
                  <span>Auto-refresh: 30s</span>
                </div>
              </div>

              {/* Lead cards grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((lead, i) => (
                  <LeadCard key={lead.id} lead={lead} index={i} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA below mockup */}
        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm mb-4">
            These are sample leads. Sign up to access the real marketplace with live inventory.
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-bold px-8 py-4 rounded-full text-base transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap"
          >
            Start Browsing Leads
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
          <p className="text-gray-400 text-xs mt-3">Free to sign up — pay only for leads you buy</p>
        </div>
      </div>
    </section>
  );
}
